import ExcelJS from 'exceljs';
import { CSV_COLUMNS, csvValues, neutralizeFormula, toCsv } from '../../../shared/riskregister/csv';
import type { Payload, Row } from '../../../shared/riskregister/types';

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function download(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Firefox and some Safari versions queue the download asynchronously, so revoking in the same
  // tick as click() cancelled it silently: no file, no error. Defer to the next macrotask.
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

export function excelCell(value: unknown): unknown {
  return typeof value === 'string' ? neutralizeFormula(value) : value;
}

function columnName(index: number): string {
  let current = index;
  let name = '';
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name;
}

export function downloadRiskRegisterCsv(payload: Payload, rows: Row[]): void {
  download(
    new Blob([toCsv(rows, payload)], { type: 'text/csv;charset=utf-8' }),
    `risk-register-${payload.as_of}.csv`,
  );
}

export async function downloadRiskRegisterExcel(payload: Payload, rows: Row[]): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Risk Register', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const header = worksheet.addRow([...CSV_COLUMNS]);
  header.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0066CC' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });

  csvValues(rows, payload).forEach((values) => {
    const row = worksheet.addRow(values.map(excelCell));
    row.alignment = { vertical: 'top', wrapText: true };
  });

  worksheet.autoFilter = `A1:${columnName(CSV_COLUMNS.length)}${rows.length + 1}`;
  worksheet.columns = CSV_COLUMNS.map((column) => ({
    width: column === 'summary' ? 48 : Math.max(12, Math.min(28, column.length + 3)),
  }));

  const buffer = await workbook.xlsx.writeBuffer();
  download(new Blob([buffer], { type: EXCEL_MIME_TYPE }), `risk-register-${payload.as_of}.xlsx`);
}
